import {
  computeDisasterEffects,
  computeFinalOutputPerType,
  sumPortfolioOutput
} from './disasters.js';
import { computeYearOpex, totalYearOpex } from './opex.js';
import { shortfallPenalty } from './shortfall.js';
import { DEMAND_BY_YEAR } from '../data/demand.js';
import { CLEAN_ENERGY_AWARD_ELIGIBLE_SET } from '../data/awards.js';
import { PLANT_TYPE } from '../data/plants.js';

export function resolveYear({
  year,
  portfolio,
  newlyBuiltThisYear,
  subsidyActive,
  disasters,
  rolledOutputs,
  plantsById,
  rng = null
}) {
  const baseDemand = DEMAND_BY_YEAR[year];

  const {
    outputMultipliers,
    affectedPlantIdsByDisasterId,
    demandAdd,
    priceShock
  } = computeDisasterEffects({
    disasters,
    portfolio,
    plantsById,
    rng,
    year
  });

  const outputPerType = computeFinalOutputPerType({
    portfolio,
    plantsById,
    rolledOutputs,
    outputMultipliers
  });

  const totalOutput = sumPortfolioOutput(portfolio, outputPerType);
  let cleanEnergyOutput = 0;
  let variableCleanEnergyOutput = 0;
  const familyOutput = {};
  for (const id of portfolio) {
    const output = outputPerType[id] ?? 0;
    if (CLEAN_ENERGY_AWARD_ELIGIBLE_SET.has(id)) {
      cleanEnergyOutput += output;
      if (plantsById[id]?.type === PLANT_TYPE.VARIABLE) {
        variableCleanEnergyOutput += output;
      }
    }
    const family = plantsById[id]?.family;
    if (family) familyOutput[family] = (familyOutput[family] ?? 0) + output;
  }

  const demand = baseDemand + demandAdd;
  const shortfall = Math.max(0, demand - totalOutput);
  const shortfallCost = shortfallPenalty(shortfall);

  const opexRows = computeYearOpex({
    portfolio,
    plantsById,
    subsidyActive,
    priceShockActive: priceShock
  });
  const opex = totalYearOpex(opexRows);

  const capital = newlyBuiltThisYear.reduce(
    (s, id) => s + plantsById[id].capital,
    0
  );

  const emissions = portfolio.reduce(
    (s, id) => s + plantsById[id].emissions,
    0
  );

  return {
    year,
    capital,
    opex,
    shortfall,
    shortfallCost,
    emissions,
    totalOutput,
    cleanEnergyOutput,
    variableCleanEnergyOutput,
    familyOutput,
    outputPerType,
    affectedPlantIdsByDisasterId,
    demandAdd,
    demand,
    priceShock,
    subsidyActive,
    disasters,
    rolledOutputs,
    opexRows
  };
}
